import apiClient from './apiClient';

const apiBaseURL = '/api/artifacts';
// The indexing status endpoint lives under a DIFFERENT base than the artifacts
// routes — it must NOT be prefixed with `apiBaseURL` (Workstream C).
const indexingBaseURL = '/api/indexing';

export type ConceptKind = 'ontology_class' | 'ontology_instance' | 'adhoc';
export type UploadStatus = 'pending' | 'ready' | 'failed';

// Download formats served by `GET /sessions/{id}/export.<format>` (GL-2.2
// added pptx).
export type ArtifactExportFormat = 'md' | 'docx' | 'pdf' | 'pptx';

// Generation strategy for the smart branded pptx endpoint (GL):
// - `autonomous` → branded deck built from the organisation charter
// - `template`   → template-constrained deck (requires a pptx `template_id`)
export type SmartPptxMode = 'autonomous' | 'template';

export interface GeneratePptxRequest {
  mode: SmartPptxMode;
  template_id: string | null;
  instructions: string | null;
}

// Body of the generic smart-generation endpoint (GL). Same choice as pptx, plus
// the target deliverable format (md/docx/pdf/pptx).
export interface GenerateArtifactFileRequest extends GeneratePptxRequest {
  format: ArtifactExportFormat;
}

export interface Fragment {
  id: string;
  text: string;
  concepts: string[];
  replaces?: string | null;
}

export interface ArtifactConcept {
  id: string;
  concept_id: string;
  label: string;
  description: string | null;
  sub_description: string | null;
  color: string;
  kind: ConceptKind;
  ontology_class_iri: string | null;
  neo4j_entity_id: string | null;
  theme_code: string | null;
  links: string[];
  depth: string[] | null;
  confidence: number;
  created_at: string;
  updated_at: string;
}

export interface ArtifactDocument {
  id: string;
  version: number;
  fragments: Fragment[];
  created_at: string;
}

export interface ArtifactUpload {
  id: string;
  filename: string;
  mime_type: string | null;
  size_bytes: number;
  status: UploadStatus;
  error_message: string | null;
  has_extracted_text: boolean;
  created_at: string;
}

export interface ArtifactSession {
  id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  // The project the session was started from (plan §5.7, L2d). Null for a
  // session started anywhere else.
  origin_project_id?: string | null;
}

// The project a session was started from, while the user can still read it.
export interface ArtifactOriginProject {
  id: string;
  title: string;
}

// Server-authoritative active domaine/usecase/cadrage/project for a session
// (Workstream A; `project` since L2d, set only when a session starts from a
// project). `kind`/`entity_id`/`label` are all null when no active context is set.
export type ActiveContextKind = 'domain' | 'usecase' | 'cadrage' | 'project';

export interface ActiveContext {
  kind: ActiveContextKind | null;
  entity_id: string | null;
  label: string | null;
}

export interface ArtifactToolCall {
  name: string;
  args: Record<string, unknown> | string;
  result_summary?: string;
}

// A clarifying question the orchestrator asks when it lacks the information to
// safely create/update a usecase (Workstream E, req 8/9). When an assistant
// message carries a non-empty `questions` array the turn ends awaiting the
// user's reply (no writes committed); the user's NEXT message answers them.
// `field` names the usecase field the answer should populate (e.g.
// "description_metier") when applicable, else null. `options` is populated only
// for `kind === 'choice'`.
export type ClarifyingQuestionKind = 'open' | 'choice';

export interface ClarifyingQuestion {
  id: string;
  text: string;
  field: string | null;
  kind: ClarifyingQuestionKind;
  options: string[] | null;
}

export interface ArtifactMessage {
  id: string;
  is_user: boolean;
  content: string;
  narration: string | null;
  tool_calls: ArtifactToolCall[] | null;
  coverage_pct: number | null;
  created_at: string;
  // Clarifying questions raised by the assistant on this turn. Defaults to `[]`
  // (the backend always sends an array, but older cached messages may omit it).
  questions?: ClarifyingQuestion[];
}

export interface ArtifactSessionDetail extends ArtifactSession {
  current_document: ArtifactDocument | null;
  concepts: ArtifactConcept[];
  uploads: ArtifactUpload[];
  messages: ArtifactMessage[];
  // Null when the session has no active context, or the referenced entity was
  // deleted and could not be resolved to a label.
  active_context: ActiveContext | null;
  // Null when the session was not started from a project, or when the user can
  // no longer read that project (the session stays usable without it).
  origin_project?: ArtifactOriginProject | null;
}

export interface SendMessageResponse {
  user_message: ArtifactMessage;
  assistant_message: ArtifactMessage;
  document: ArtifactDocument | null;
  concepts: ArtifactConcept[];
  session?: ArtifactSession | null;
}

export interface ConceptUpsert {
  id: string;
  label: string;
  description?: string | null;
  sub_description?: string | null;
  links?: string[];
}

export interface AppendDocumentRequest {
  fragments: Fragment[];
  new_concepts?: ConceptUpsert[];
}

// Indexing lifecycle of a KB document attached to the active entity. Mirrors
// the backend `Document.indexing_status` enum. `not_indexed` = never queued;
// `pending`/`indexing` = in-flight (Workstream C surfaces live progress);
// `indexed` = searchable; `failed` = indexing errored.
export type DocumentIndexingStatus =
  | 'not_indexed'
  | 'pending'
  | 'indexing'
  | 'indexed'
  | 'failed';

export interface RelatedDocument {
  id: number;
  name: string;
  mime_type: string | null;
  indexing_status: DocumentIndexingStatus;
  indexed_at: string | null;
  folder_path: string | null;
}

export interface RelatedDocumentsResponse {
  active_context: ActiveContext | null;
  documents: RelatedDocument[];
}

// Where an artifact was saved (Workstream C). `custom_folder` with label
// "artifacts" is the fallback target when the session has no active context.
export type SaveTargetKind = 'domain' | 'usecase' | 'cadrage' | 'custom_folder';

export interface SaveTarget {
  kind: SaveTargetKind;
  label: string;
}

// Response of `POST /sessions/{id}/save-document`. `indexing_triggered` is true
// only for an "Add to KG" save (`index: true`) that actually queued indexing.
export interface SaveDocumentResponse {
  document: RelatedDocument;
  target: SaveTarget;
  indexing_triggered: boolean;
}

// Live indexing status of a KB document (Workstream C). Served from a base
// distinct from the artifacts routes (`/api/indexing`).
export interface IndexingStatusResponse {
  document_id: number;
  status: DocumentIndexingStatus;
  chunks_count: number | null;
  indexed_at: string | null;
  error_message: string | null;
  neo4j_database: string | null;
}

// Extract the download filename from a Content-Disposition header. RFC 5987:
// prefer filename* (URL-encoded UTF-8), fall back to filename=. Accepts the raw
// axios headers object (typed `unknown` to stay independent of the axios header
// type across versions).
const parseContentDispositionFilename = (headers: unknown, fallback: string): string => {
  const bag = (headers ?? {}) as Record<string, unknown>;
  const cd = (bag['content-disposition'] || bag['Content-Disposition'] || '') as string;
  const utf8Match = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const asciiMatch = cd.match(/filename="?([^";]+)"?/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      return fallback;
    }
  }
  if (asciiMatch?.[1]) {
    return asciiMatch[1];
  }
  return fallback;
};

export const artifactsService = {
  async listSessions(skip = 0, limit = 20): Promise<ArtifactSession[]> {
    const r = await apiClient.get(`${apiBaseURL}/sessions`, { params: { skip, limit } });
    return r.data;
  },
  // With `projectId`, the server checks the project is readable (404 otherwise),
  // titles the session after it, sets it as the active context and writes a
  // welcome message — the session opens with that message already there.
  async createSession(
    title: string | null = null,
    projectId: string | null = null
  ): Promise<ArtifactSession> {
    const body = projectId ? { title, project_id: projectId } : { title };
    const r = await apiClient.post(`${apiBaseURL}/sessions`, body);
    return r.data;
  },
  async getSession(sessionId: string): Promise<ArtifactSessionDetail> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}`);
    return r.data;
  },
  async updateSession(sessionId: string, title: string | null): Promise<ArtifactSession> {
    const r = await apiClient.patch(`${apiBaseURL}/sessions/${sessionId}`, { title });
    return r.data;
  },
  async deleteSession(sessionId: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}/sessions/${sessionId}`);
  },
  async appendDocument(
    sessionId: string,
    payload: AppendDocumentRequest
  ): Promise<ArtifactDocument> {
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/documents`, payload);
    return r.data;
  },
  async listConcepts(sessionId: string): Promise<ArtifactConcept[]> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}/concepts`);
    return r.data;
  },
  async listUploads(sessionId: string): Promise<ArtifactUpload[]> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}/uploads`);
    return r.data;
  },
  async uploadFile(sessionId: string, file: File): Promise<ArtifactUpload> {
    const data = new FormData();
    data.append('file', file);
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/uploads`, data);
    return r.data;
  },
  async deleteUpload(sessionId: string, uploadId: string): Promise<void> {
    await apiClient.delete(`${apiBaseURL}/sessions/${sessionId}/uploads/${uploadId}`);
  },
  async listMessages(sessionId: string): Promise<ArtifactMessage[]> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}/messages`);
    return r.data;
  },
  async sendMessage(sessionId: string, content: string): Promise<SendMessageResponse> {
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/messages`, { content });
    return r.data;
  },
  // KB documents attached to the session's active entity (Workstream B). The
  // backend endpoint is built in parallel; callers should treat a 404/empty
  // response as "no related documents" and render the empty state.
  async getRelatedDocuments(sessionId: string): Promise<RelatedDocumentsResponse> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}/related-documents`);
    return r.data;
  },
  // Save the session's current document to the KB (Workstream C, req 3/4).
  // `index: true` = "Add to KG" (save + queue indexing); `index: false` =
  // "Save as doc" (save only). Idempotent — re-saving overwrites the same
  // document row (no duplicates).
  async saveDocument(sessionId: string, index: boolean): Promise<SaveDocumentResponse> {
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/save-document`, { index });
    return r.data;
  },
  // Poll the live indexing status of a saved KB document (Workstream C). Built
  // against `indexingBaseURL`, NOT the artifacts base.
  async getIndexingStatus(documentId: number): Promise<IndexingStatusResponse> {
    const r = await apiClient.get(`${indexingBaseURL}/documents/${documentId}/status`);
    return r.data;
  },
  async exportSession(
    sessionId: string,
    format: ArtifactExportFormat
  ): Promise<{ blob: Blob; filename: string }> {
    const r = await apiClient.get(`${apiBaseURL}/sessions/${sessionId}/export.${format}`, {
      responseType: 'blob',
    });
    const blob = r.data as Blob;
    const filename = parseContentDispositionFilename(r.headers, `artifact-${sessionId}.${format}`);
    return { blob, filename };
  },
  // Intelligent branded PowerPoint generation (GL). `mode: 'autonomous'` builds a
  // branded deck from the org charter; `mode: 'template'` fills a pptx template
  // (requires `template_id`). Returns the .pptx blob and its server filename.
  async generatePptx(
    sessionId: string,
    payload: GeneratePptxRequest
  ): Promise<{ blob: Blob; filename: string }> {
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/generate-pptx`, payload, {
      responseType: 'blob',
    });
    const blob = r.data as Blob;
    const filename = parseContentDispositionFilename(r.headers, `presentation-${sessionId}.pptx`);
    return { blob, filename };
  },
  // Smart document generation for any format (GL). `mode: 'autonomous'` synthesises
  // a structured document (a branded deck for pptx); `mode: 'template'` constrains
  // the output to an org document template (requires `template_id`). Returns the
  // deliverable blob and its server filename.
  async generateFile(
    sessionId: string,
    payload: GenerateArtifactFileRequest
  ): Promise<{ blob: Blob; filename: string }> {
    const r = await apiClient.post(`${apiBaseURL}/sessions/${sessionId}/generate-file`, payload, {
      responseType: 'blob',
    });
    const blob = r.data as Blob;
    const filename = parseContentDispositionFilename(
      r.headers,
      `document-${sessionId}.${payload.format}`
    );
    return { blob, filename };
  },
};
